import { useCallback, useState } from 'react';

import {
  Typography, AppBar, Toolbar, IconButton, Paper, Stack, Chip, Button, Box,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffectAsync } from '../reactHelper';
import { useTranslation } from '../common/components/LocalizationProvider';
import MapView from '../map/core/MapView';
import MapCamera from '../map/MapCamera';
import MapPositions from '../map/MapPositions';
import MapGeofence from '../map/MapGeofence';
import StatusCard from '../common/components/StatusCard';
import { formatNotificationTitle } from '../common/util/formatter';
import MapScale from '../map/MapScale';
import BackIcon from '../common/components/BackIcon';
import fetchOrThrow from '../common/util/fetchOrThrow';

const useStyles = makeStyles()(() => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  toolbar: {
    zIndex: 1,
  },
  mapContainer: {
    flexGrow: 1,
    position: 'relative',
  },
  clipsPanel: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    right: 8,
    zIndex: 2,
    maxHeight: '30%',
    overflow: 'auto',
  },
}));

const STATUS_COLORS = {
  pending: 'default',
  recording: 'warning',
  ready: 'success',
  error: 'error',
};

const STATUS_LABELS = {
  pending: 'Pendente',
  recording: 'Gravando',
  ready: 'Pronto',
  error: 'Erro',
};

const CHANNEL_NAMES = {
  1: 'Frontal',
  2: 'Motorista',
  3: 'Lateral D',
  4: 'Lateral E',
};

const EventPage = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const t = useTranslation();

  const { id } = useParams();

  const [event, setEvent] = useState();
  const [position, setPosition] = useState();
  const [showCard, setShowCard] = useState(false);
  const [clips, setClips] = useState([]);
  const [playerUrl, setPlayerUrl] = useState(null);

  const formatType = (event) => formatNotificationTitle(t, {
    type: event.type,
    attributes: {
      alarms: event.attributes.alarm,
    },
  });

  const onMarkerClick = useCallback((positionId) => {
    setShowCard(Boolean(positionId));
  }, [setShowCard]);

  useEffectAsync(async () => {
    if (id) {
      const response = await fetchOrThrow(`/api/events/${id}`);
      setEvent(await response.json());
    }
  }, [id]);

  useEffectAsync(async () => {
    if (event && event.positionId) {
      const response = await fetchOrThrow(`/api/positions?id=${event.positionId}`);
      const positions = await response.json();
      if (positions.length > 0) {
        setPosition(positions[0]);
      }
    }
  }, [event]);

  // Fetch clips for this event
  useEffectAsync(async () => {
    if (id) {
      try {
        const response = await fetch(`/api/events/${id}/clip`);
        if (response.ok) {
          const data = await response.json();
          setClips(Array.isArray(data) ? data : [data]);
        }
      } catch {
        // no clips
      }
    }
  }, [id]);

  const handleRetry = async (clipId) => {
    await fetch(`/api/events/clips/${clipId}/retry`, { method: 'POST' });
    const response = await fetch(`/api/events/${id}/clip`);
    if (response.ok) {
      const data = await response.json();
      setClips(Array.isArray(data) ? data : [data]);
    }
  };

  return (
    <div className={classes.root}>
      <AppBar color="inherit" position="static" className={classes.toolbar}>
        <Toolbar>
          <IconButton color="inherit" edge="start" sx={{ mr: 2 }} onClick={() => navigate('/')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>{event && formatType(event)}</Typography>
          {event && (
            <IconButton color="primary" onClick={() => navigate(`/camera/clips/${event.deviceId}`)}>
              <VideocamIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      <div className={classes.mapContainer}>
        <MapView>
          <MapGeofence />
          {position && <MapPositions positions={[position]} onMarkerClick={onMarkerClick} titleField="fixTime" />}
        </MapView>
        <MapScale />
        {position && <MapCamera latitude={position.latitude} longitude={position.longitude} />}
        {position && showCard && (
          <StatusCard
            deviceId={position.deviceId}
            position={position}
            onClose={() => setShowCard(false)}
            disableActions
          />
        )}

        {/* Clips panel */}
        {clips.length > 0 && (
          <Paper className={classes.clipsPanel} elevation={3} sx={{ p: 1.5 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={600}>
                <VideocamIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Clips de Video (
                {clips.length}
                )
              </Typography>
              {clips.map((clip) => (
                <Stack key={clip.id} direction="row" alignItems="center" spacing={1}>
                  <Chip
                    size="small"
                    label={`CH${clip.channel} ${CHANNEL_NAMES[clip.channel] || ''}`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={STATUS_LABELS[clip.status] || clip.status}
                    color={STATUS_COLORS[clip.status] || 'default'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {clip.duration}
                    s
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {clip.status === 'ready' && clip.clipUrl && (
                    <>
                      <Button
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => setPlayerUrl(clip.clipUrl)}
                      >
                        Play
                      </Button>
                      <IconButton
                        size="small"
                        component="a"
                        href={`/api/events/clips/${clip.id}/download`}
                        target="_blank"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  {clip.status === 'error' && (
                    <Button size="small" startIcon={<ReplayIcon />} onClick={() => handleRetry(clip.id)}>
                      Retry
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Inline video player */}
        {playerUrl && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              right: 8,
              zIndex: 3,
              backgroundColor: '#000',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <video
                src={playerUrl}
                controls
                autoPlay
                style={{ width: '100%', maxHeight: '50vh' }}
              />
              <IconButton
                size="small"
                onClick={() => setPlayerUrl(null)}
                sx={{
                  position: 'absolute', top: 4, right: 4, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)',
                }}
              >
                ✕
              </IconButton>
            </Box>
          </Paper>
        )}
      </div>
    </div>
  );
};

export default EventPage;
